import { useEffect, useRef } from 'react';
import type { CanvasState } from './useCanvasTransform';
import type { CanvasObject } from '../types';
import type { AABB } from '../utils/aabb';

export function useRotateSelected(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  getCanvasState: () => CanvasState,
  getActiveTool: () => 'select' | 'rectangle' | 'circle' | 'text' | 'artboard',
  selectedId: string | null,
  getObjectById: (id: string | null) => CanvasObject | null,
  updateObject: (id: string, updater: (obj: CanvasObject) => CanvasObject) => void,
  requestRender: () => void,
  shouldBlock?: () => boolean,
  isRotatingRef?: React.MutableRefObject<boolean>,
  onWorkComplete?: () => void,
  // 다중 선택 관련 파라미터
  selectedIds?: Set<string>,
  getSelectedObjects?: () => CanvasObject[],
  getBbox?: () => AABB | null,
  rotateSelectedBy?: (centerX: number, centerY: number, deltaAngle: number, initialStates: Map<string, { x: number; y: number; width: number; height: number; rotation: number }>) => void
) {
  const rotatingRef = useRef(false);
  const centerRef = useRef<{ x: number; y: number } | null>(null);
  const startAngleRef = useRef<number | null>(null);
  const baseRotationRef = useRef<number>(0);
  const isMultiSelectRef = useRef(false);
  const initialObjectsRef = useRef<Map<string, { x: number; y: number; width: number; height: number; rotation: number }> | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toWorld = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const cx = clientX - rect.left;
      const cy = clientY - rect.top;
      const { scale, offset } = getCanvasState();
      return { x: (cx - offset.x) / scale, y: (cy - offset.y) / scale };
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (getActiveTool() !== 'select') return;
      if (shouldBlock && shouldBlock()) return;
      
      // 다중 선택 모드 체크
      const isMultiSelect = !!(selectedIds && selectedIds.size > 1);
      if (!selectedId && !isMultiSelect) return;
      
      const { scale, offset } = getCanvasState();
      const screenRect = canvas.getBoundingClientRect();
      const sx = e.clientX - screenRect.left;
      const sy = e.clientY - screenRect.top;
      const handleOffset = 24 / scale;
      const handleRadius = 12; // pixels
      
      let cx: number, cy: number, angle: number;
      let handleWorldX: number, handleWorldY: number;
      
      if (isMultiSelect && getBbox) {
        // 다중 선택 모드: AABB 기준
        const bbox = getBbox();
        if (!bbox) return;
        cx = bbox.x + bbox.width / 2;
        cy = bbox.y + bbox.height / 2;
        angle = 0; // AABB는 회전되지 않음
        
        // AABB 상단 중앙 위에 회전 핸들 위치
        handleWorldX = cx;
        handleWorldY = bbox.y - handleOffset;
      } else if (selectedId) {
        // 단일 선택 모드: 객체 기준
        const obj = getObjectById(selectedId);
        if (!obj) return;
        cx = obj.x + obj.width / 2;
        cy = obj.y + obj.height / 2;
        angle = obj.rotation || 0;
        
        // 로컬 좌표계에서 회전 핸들 위치 (상단 중앙 위)
        const localHandleX = 0;
        const localHandleY = -obj.height / 2 - handleOffset;
        
        // 회전 변환 적용
        if (Math.abs(angle) > 1e-6) {
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          handleWorldX = cx + localHandleX * cos - localHandleY * sin;
          handleWorldY = cy + localHandleX * sin + localHandleY * cos;
        } else {
          handleWorldX = cx + localHandleX;
          handleWorldY = cy + localHandleY;
        }
      } else {
        return;
      }
      
      // 화면 좌표로 변환
      const hx = handleWorldX * scale + offset.x;
      const hy = handleWorldY * scale + offset.y;
      const dx = sx - hx;
      const dy = sy - hy;
      if (dx * dx + dy * dy > handleRadius * handleRadius) return;

      // start rotating
      e.stopPropagation();
      e.preventDefault();
      rotatingRef.current = true;
      if (isRotatingRef) {
        isRotatingRef.current = true;
      }
      
      centerRef.current = { x: cx, y: cy };
      isMultiSelectRef.current = isMultiSelect;
      
      if (isMultiSelect && getSelectedObjects && rotateSelectedBy) {
        // 다중 선택: 초기 상태 저장
        const selectedObjs = getSelectedObjects();
        const initialStates = new Map<string, { x: number; y: number; width: number; height: number; rotation: number }>();
        selectedObjs.forEach(obj => {
          initialStates.set(obj.id, {
            x: obj.x,
            y: obj.y,
            width: obj.width,
            height: obj.height,
            rotation: obj.rotation || 0
          });
        });
        initialObjectsRef.current = initialStates;
        baseRotationRef.current = 0; // 다중 선택은 그룹 회전이므로 0부터 시작
      } else if (selectedId) {
        // 단일 선택
        const obj = getObjectById(selectedId);
        if (obj) {
          baseRotationRef.current = obj.rotation || 0;
        }
        initialObjectsRef.current = null;
      }
      
      const p0 = toWorld(e.clientX, e.clientY);
      startAngleRef.current = Math.atan2(p0.y - cy, p0.x - cx);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!rotatingRef.current) return;
      const center = centerRef.current;
      const startAng = startAngleRef.current;
      if (!center || startAng == null) return;
      
      const p = toWorld(e.clientX, e.clientY);
      const curAng = Math.atan2(p.y - center.y, p.x - center.x);
      let delta = curAng - startAng;
      
      // 스냅 처리
      const stepDeg = e.shiftKey ? 15 : 1; // Shift: 15도, 기본: 1도
      const totalRot = baseRotationRef.current + delta;
      const rotDeg = totalRot * (180 / Math.PI);
      const snappedDeg = Math.round(rotDeg / stepDeg) * stepDeg;
      const snappedRot = snappedDeg * (Math.PI / 180);
      const snappedDelta = snappedRot - baseRotationRef.current;
      
      if (isMultiSelectRef.current && rotateSelectedBy && initialObjectsRef.current) {
        // 다중 선택: rotateSelectedBy 사용 (총 회전 각도 전달)
        // baseRotationRef.current는 0이므로 snappedDelta가 총 회전 각도
        rotateSelectedBy(center.x, center.y, snappedDelta, initialObjectsRef.current);
      } else if (selectedId) {
        // 단일 선택: 기존 로직
        const obj = getObjectById(selectedId);
        if (obj) {
          const newRot = baseRotationRef.current + snappedDelta;
          updateObject(obj.id, (o) => ({ ...o, rotation: newRot }));
        }
      }
      
      requestRender();
    };

    const onMouseUp = () => {
      if (!rotatingRef.current) return;
      const wasRotating = rotatingRef.current;
      rotatingRef.current = false;
      centerRef.current = null;
      startAngleRef.current = null;
      isMultiSelectRef.current = false;
      initialObjectsRef.current = null;
      if (isRotatingRef) {
        isRotatingRef.current = false;
      }
      // 회전이 실제로 발생했으면 히스토리 저장
      if (wasRotating && onWorkComplete) {
        onWorkComplete();
      }
    };

    canvas.addEventListener('mousedown', onMouseDown, { capture: true });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown, { capture: true } as any);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [canvasRef, getCanvasState, getActiveTool, selectedId, getObjectById, updateObject, requestRender, shouldBlock, isRotatingRef, onWorkComplete, selectedIds, getSelectedObjects, getBbox, rotateSelectedBy]);
}
